import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import VirtualList from 'react-tiny-virtual-list';

import { Divider, Modal, NavBar } from '@/components';
import { IconMovements } from '@/components/Icons/IconMovements';
import { Paginator } from '@/components/Paginator';
import { useUndepsHostbackButton } from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import {
  initMyAccount,
  calculateIcon,
  getETipoPinById,
  ETipoPin,
  getEstadoByPinId,
  EnumTipoProducto,
  PIN_TDA_TITLE,
} from '@/services/myAccount';
import { getDetailPinPackage } from '@/services/pins';
import {
  DetalleMovimientoCuentaMobileTO,
  PinTO,
  ResponseDetallePaquetePin,
} from '@/types/myAccount';
import {
  cn,
  datejs,
  formatToBalanceArs,
  MANUAL_PIN_LOAD_ID,
  PACKS_BY_ID,
} from '@/utils';

const getMovementType = (movement: DetalleMovimientoCuentaMobileTO) => {
  if (movement) {
    return calculateIcon(movement);
  }
};

const getModalTitle = (
  movement: DetalleMovimientoCuentaMobileTO | null
): string => {
  if (!movement) return '';

  const isManualLoad = movement.tipoProductoGDT === MANUAL_PIN_LOAD_ID;
  const description = movement.responsive?.descripcion || '';
  const etipoPin = getETipoPinById(movement.idTipoPin);

  if (!etipoPin) return description;
  if (isManualLoad) return description;

  const { esTap, id, txt1 } = etipoPin;

  if (esTap) return PIN_TDA_TITLE;

  const relevantPins = [ETipoPin.PIN_FEORO.id, ETipoPin.PIN_WEB.id];

  if (relevantPins.includes(id)) return txt1;

  return description;
};

const isItemAvailableToClick = (movement: DetalleMovimientoCuentaMobileTO) => {
  const etipoPin = getETipoPinById(movement.idTipoPin);
  const isPackageMovement = movement.pack;
  const isFriendsTournament = etipoPin?.esTap;
  const isGoldenDate = etipoPin?.id === ETipoPin.PIN_FEORO.id;

  const isLoadBalance = 'idTipoIngreso' in movement;
  const isF5Movement = etipoPin?.id === ETipoPin.PIN_F5.id;
  const isAMovement = etipoPin?.id === ETipoPin.PIN_A.id;
  const is1Pin = movement.tipoProductoGDT === PACKS_BY_ID.PACK_1;
  const isMoneyMovement = !etipoPin;
  const isManualLoad = movement.tipoProductoGDT === MANUAL_PIN_LOAD_ID;
  const is1PinEdicionPasada =
    movement.tipoProductoGDT === PACKS_BY_ID.PIN_EDICION_PASADA;

  return (
    isPackageMovement ||
    isFriendsTournament ||
    isGoldenDate ||
    isLoadBalance ||
    isF5Movement ||
    isAMovement ||
    is1Pin ||
    isMoneyMovement ||
    isManualLoad ||
    is1PinEdicionPasada
  );
};

function buildDetailsObject(
  movement: DetalleMovimientoCuentaMobileTO | null
): ResponseDetallePaquetePin | undefined {
  if (movement?.pinCodigo) {
    return {
      pines: [
        {
          codigo: movement.pinCodigo,
          estado: getEstadoByPinId(movement.idEstadoItem),
        },
      ],
      mensaje: '',
      estado: false,
    };
  }
}

type MovementsProps = {
  movements: DetalleMovimientoCuentaMobileTO[];
};

const usePinsMovementDetail = (
  selectedMovement: DetalleMovimientoCuentaMobileTO | null
) => {
  return useQuery(
    'getDetailPinPackage',
    async () => await getDetailPinPackage(selectedMovement?.id || -1),
    {
      enabled: !!selectedMovement && selectedMovement.pack,
      refetchOnWindowFocus: false,
    }
  );
};

type MovementDetailListProps = {
  items?: PinTO[];
};

const MovementDetailList = React.memo(({ items }: MovementDetailListProps) => {
  const { isDesktop } = useIsDesktop();
  let classToAdd = 'max-h-[300px] mt-[20px]';
  if (items?.length && items?.length > 4) classToAdd += ' overflow-y-scroll';
  if (isDesktop) {
    classToAdd += ' pr-[20px]';
  }
  return (
    <ul className={classToAdd}>
      {items?.map((pin, index) => {
        const redStyles = {
          backgroundColor: 'rgb(255, 237, 237)',
          color: 'rgb(216, 70, 56)',
        };

        const greenStyles = {
          backgroundColor: 'rgb(212, 245, 238)',
          color: 'rgb(38, 127, 95)',
        };
        return (
          <>
            <li
              key={index}
              className={'flex items-center justify-between h-[60px] '}
            >
              <span>
                <span className={'text-base font-bold'}>PIN:</span> {pin.codigo}
              </span>
              <div
                className={cn(
                  'text-s font-semibold inline-flex items-center gap-2 px-[8px] rounded-[8px] bg-gdt-blue-xl2 py-[4px]'
                )}
                style={
                  pin.estado?.toUpperCase() === 'VIGENTE'
                    ? greenStyles
                    : redStyles
                }
              >
                {pin.estado?.toUpperCase()}
              </div>
            </li>
            <Divider type="horizontal" />
          </>
        );
      })}
    </ul>
  );
});

MovementDetailList.displayName = 'MovementDetailList';

const ITEM_PER_PAGE = 6;
const MovementsDesktop = React.memo(({ movements }: MovementsProps) => {
  const [selectedMovement, setSelectedMovement] =
    useState<DetalleMovimientoCuentaMobileTO | null>(null);
  const [currentMovements, setCurrentMovements] = useState<
    DetalleMovimientoCuentaMobileTO[]
  >([]);

  const handleChangePage = (movements: DetalleMovimientoCuentaMobileTO[]) => {
    setCurrentMovements(movements);
  };

  // INIT PAGINATOR
  useEffect(() => {
    handleChangePage(movements.slice(0, ITEM_PER_PAGE));
  }, [movements]);

  useUndepsHostbackButton(() => {
    setSelectedMovement(null);
  });

  const handleClickItem = (movement: DetalleMovimientoCuentaMobileTO) => {
    const canClick = isItemAvailableToClick(movement);

    if (canClick) {
      setSelectedMovement(movement);
    }
  };
  let { data: dataPinsMovementsDetail, isSuccess } =
    usePinsMovementDetail(selectedMovement);

  const model = selectedMovement?.pack
    ? dataPinsMovementsDetail
    : buildDetailsObject(selectedMovement);

  if (selectedMovement?.idTipoIngreso) {
    isSuccess = true;
  }
  const pines = model?.pines ?? [];

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px' }}>
      {currentMovements.map(
        (movement: DetalleMovimientoCuentaMobileTO, index: number) => (
          <div
            key={index}
            className={cn({
              'hover:cursor-pointer': isItemAvailableToClick(movement),
            })}
            onClick={() => handleClickItem(movement)}
          >
            <MovementItem movement={movement} />
          </div>
        )
      )}
      {movements.length > 6 && (
        <Paginator
          key={movements.length}
          currentContentPage={handleChangePage}
          items={movements}
          itemsPerPage={ITEM_PER_PAGE}
        />
      )}

      {(isSuccess ||
        pines.length > 0 ||
        selectedMovement?.tipoProductoGDT ===
          EnumTipoProducto.TRANSFERIR_SALDO.id) && (
        <Modal
          className={'pb-[0] px-[30px] pt-[20px]'}
          controlledOnClose={() => setSelectedMovement(null)}
          isControlledOpen={Boolean(selectedMovement)}
          render={() => {
            if (
              selectedMovement?.tipoProductoGDT ===
              EnumTipoProducto.TRANSFERIR_SALDO.id
            ) {
              return (
                <div className={'flex p-[20px] justify-center'}>
                  {selectedMovement.monto?.includes('-') && (
                    <div>
                      Enviaste ${Math.abs(parseInt(selectedMovement.monto, 10))}
                      {' a '}
                      {selectedMovement.usuarioTransferencia}
                    </div>
                  )}
                  {selectedMovement.monto &&
                    !selectedMovement.monto?.includes('-') && (
                      <div>
                        Recibiste ${parseInt(selectedMovement.monto, 10)}
                        {' de '}
                        {selectedMovement.usuarioTransferencia}
                      </div>
                    )}
                </div>
              );
            } else if (selectedMovement?.idTipoIngreso) {
              return (
                <div className={'flex p-[20px] justify-center'}>
                  {selectedMovement.descripcion}
                </div>
              );
            } else if (pines.length > 0) {
              return <MovementDetailList items={pines} />;
            }
            return null;
          }}
          size="xs"
          title={getModalTitle(selectedMovement)}
          variant="controlled"
        />
      )}
    </div>
  );
});

MovementsDesktop.displayName = 'MovementsDesktop';

function MovementsMobile({ movements }: MovementsProps) {
  const [selectedMovement, setSelectedMovement] =
    useState<DetalleMovimientoCuentaMobileTO | null>(null);

  const navigate = useNavigate();

  const ITEM_SIZE = 70;

  const handleClickItem = (movement: DetalleMovimientoCuentaMobileTO) => {
    if (isItemAvailableToClick(movement)) {
      setSelectedMovement(movement);
    }
  };

  useUndepsHostbackButton(() => {
    setSelectedMovement(null);
  });

  let { data: dataPinsMovementsDetail, isSuccess } =
    usePinsMovementDetail(selectedMovement);

  const model = selectedMovement?.pack
    ? dataPinsMovementsDetail
    : buildDetailsObject(selectedMovement);

  if (selectedMovement?.idTipoIngreso) {
    isSuccess = true;
  }

  const pines = model?.pines ?? [];

  return (
    <>
      <div className="flex flex-col">
        <NavBar
          mobile={{
            leftContent: {
              backAction: () => {
                navigate(ROUTES.myAccount, {
                  replace: true,
                });
              },
            },
            centerContent: { title: 'Movimientos de mi cuenta' },
          }}
        >
          <VirtualList
            height={window.innerHeight - 51}
            itemCount={movements.length}
            itemSize={ITEM_SIZE}
            renderItem={({ index, style }) => {
              const currentMovement = movements[index];
              return (
                <div
                  key={index}
                  style={style}
                  onClick={() => handleClickItem(currentMovement)}
                >
                  <MovementItem movement={currentMovement} />
                </div>
              );
            }}
            width="100%"
          />
          {(isSuccess ||
            pines.length > 0 ||
            selectedMovement?.tipoProductoGDT ===
              EnumTipoProducto.TRANSFERIR_SALDO.id) && (
            <Modal
              controlledOnClose={() => setSelectedMovement(null)}
              isControlledOpen={Boolean(selectedMovement)}
              render={() => {
                if (selectedMovement?.tipoProductoGDT === 26) {
                  return (
                    <div className={'flex p-[20px] justify-center'}>
                      {selectedMovement.monto?.includes('-') && (
                        <div>
                          Enviaste $
                          {Math.abs(parseInt(selectedMovement.monto, 10))}
                          {' a '}
                          {selectedMovement.usuarioTransferencia}
                        </div>
                      )}
                      {selectedMovement.monto &&
                        !selectedMovement.monto?.includes('-') && (
                          <div>
                            Recibiste ${parseInt(selectedMovement.monto, 10)}
                            {' de '}
                            {selectedMovement.usuarioTransferencia}
                          </div>
                        )}
                    </div>
                  );
                } else if (selectedMovement?.idTipoIngreso) {
                  return (
                    <div className={'flex p-[20px] justify-center'}>
                      {selectedMovement.descripcion}
                    </div>
                  );
                } else if (model?.pines) {
                  return <MovementDetailList items={model.pines} />;
                }
                return null;
              }}
              size="xs"
              title={getModalTitle(selectedMovement)}
              variant="controlled"
            />
          )}
        </NavBar>
      </div>
    </>
  );
}

type PropsMovementItem = {
  movement: DetalleMovimientoCuentaMobileTO;
};

function MovementItem({ movement }: PropsMovementItem) {
  const { isDesktop } = useIsDesktop();
  const priceString = movement.monto ?? '0';
  const price = parseFloat(priceString);
  const priceData =
    price < 0
      ? '-$' + formatToBalanceArs(Math.abs(price))
      : '$' + formatToBalanceArs(price);
  const currentYear = new Date().getFullYear();
  const movementYear = datejs(movement.fecha, 'DD/MM/YYYY', 'es').year();

  let dateFormat = 'D [de] MMM';
  if (isDesktop) {
    dateFormat += 'M';
  }

  if (currentYear > movementYear) {
    dateFormat += ' YYYY';
  }

  const date = datejs(movement.fecha, 'DD/MM/YYYY', 'es').format(dateFormat);
  const description =
    movement.tipoProductoGDT !== PACKS_BY_ID.PIN_EDICION_PASADA
      ? movement.responsive?.descripcion
      : '1 Pin edición anterior';
  const movementType = getMovementType(movement);

  return (
    <div
      className={
        'flex pl-[30px] pr-[20px] py-[17px] after:content-[""] relative after:block after:h-px after:w-full after:border-b after:border-D2D6D6-500 after:absolute after:bottom-0 after:left-0'
      }
    >
      <div className={'basis-[60px]'}>
        {movementType && <IconMovements movementType={movementType} />}
      </div>
      <div className="flex flex-grow font-bold items-center">
        <div className="flex flex-col">
          <span>{description}</span>
          {isDesktop && !movement.pack && movement.pinCodigo.length > 0 && (
            <span className="font-normal">{movement.pinCodigo}</span>
          )}
        </div>
      </div>
      {isDesktop && (
        <div className={'flex text-gdt-gray-3 items-center font-medium'}>
          {date}
        </div>
      )}
      <div
        className={cn(['basis-[100px] flex flex-col'], {
          'justify-center flex': isDesktop,
          'items-end': true,
        })}
      >
        <div className={' text-gdt-gray-2 font-bold text-base'}>
          {priceData}
        </div>
        {!isDesktop && <div className={'text-xs text-gdt-gray-4'}>{date}</div>}
      </div>
    </div>
  );
}

export function AccountMovements() {
  const { isDesktop, isMobile } = useIsDesktop();

  const {
    data: dataInitMyAccount,
    isLoading,
    isSuccess,
  } = useQuery('init-my-account', async () => initMyAccount(), {
    refetchOnWindowFocus: false,
  });

  if (isLoading || dataInitMyAccount === undefined) return null;
  if (!dataInitMyAccount.estado) return null;

  const movements = dataInitMyAccount.movimientos ?? [];

  return (
    <>
      {isSuccess && (
        <>
          {isDesktop && <MovementsDesktop movements={movements} />}

          {isMobile && <MovementsMobile movements={movements} />}
        </>
      )}
    </>
  );
}
